import { useEffect, useState, useCallback } from "react";
import { Wallet, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINRFull, formatDate } from "@/lib/format";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const empty = { date: "", amount: "", mode: "Bank Transfer", reference: "", invoice_id: "", notes: "" };

export function PaymentsPanel({ projectId, onChanged }) {
  const { meta } = useAuth();
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [del, setDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, inv] = await Promise.all([
        api.get(`/projects/${projectId}/payments`),
        api.get(`/projects/${projectId}/invoices`),
      ]);
      setPayments(p.data); setInvoices(inv.data);
    } finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ ...empty, date: new Date().toISOString().slice(0, 10) }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    const amount = Number(form.amount) || 0;
    if (amount <= 0) { toast.error("Payment amount must be greater than 0"); return; }
    setSaving(true);
    const payload = { date: form.date, amount, mode: form.mode, reference: form.reference || null, invoice_id: form.invoice_id || null, notes: form.notes || null };
    try {
      await api.post(`/projects/${projectId}/payments`, payload);
      toast.success("Payment recorded");
      setOpen(false); load(); onChanged && onChanged();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => { await api.delete(`/payments/${del.id}`); toast.success("Payment deleted"); setDel(null); load(); onChanged && onChanged(); };

  const invoicedTotal = invoices.reduce((s, i) => s + (i.total || 0), 0);
  const receivedTotal = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const balance = invoicedTotal - receivedTotal;
  const invNo = (id) => invoices.find((i) => i.id === id)?.invoice_no;

  return (
    <div className="space-y-4" data-testid="payments-panel">
      <div className="grid grid-cols-3 gap-3">
        <SummaryCard label="Invoiced" value={formatINRFull(invoicedTotal)} color="#0057A8" />
        <SummaryCard label="Received" value={formatINRFull(receivedTotal)} color="#059669" />
        <SummaryCard label="Balance Due" value={formatINRFull(balance)} color={balance > 0 ? "#EA580C" : "#059669"} />
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Wallet className="h-4 w-4 text-[#0057A8]" /><h3 className="text-base font-semibold text-slate-800">Payments Received</h3></div>
        <Button data-testid="add-payment-btn" onClick={openNew} size="sm" className="gap-1.5 font-semibold"><Plus className="h-4 w-4" /> Record Payment</Button>
      </div>

      {loading ? (
        <div className="h-32 animate-pulse rounded-xl bg-slate-100" />
      ) : payments.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white py-12 text-center">
          <p className="text-sm font-semibold text-slate-700">No payments recorded yet</p>
          <p className="mt-1 text-xs text-slate-400">Record a payment received to track collections against invoices.</p>
          <Button onClick={openNew} className="mt-4 gap-1.5"><Plus className="h-4 w-4" /> Record First Payment</Button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-100 bg-slate-50/60 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3">Date</th><th className="px-4 py-3">Mode</th><th className="px-4 py-3">Reference</th><th className="px-4 py-3">Invoice</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3"></th>
            </tr></thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} data-testid={`payment-row-${p.id}`} className="border-b border-slate-50">
                  <td className="px-4 py-3 text-slate-600">{formatDate(p.date)}</td>
                  <td className="px-4 py-3"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">{p.mode}</span></td>
                  <td className="px-4 py-3 text-xs text-slate-500">{p.reference || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{invNo(p.invoice_id) || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-600">{formatINRFull(p.amount)}</td>
                  <td className="px-4 py-3 text-right"><button onClick={() => setDel(p)} data-testid={`delete-payment-${p.id}`} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr><td colSpan={4} className="px-4 py-3 text-right text-sm font-semibold text-slate-500">Total Received</td><td className="px-4 py-3 text-right font-mono text-base font-extrabold text-emerald-600">{formatINRFull(receivedTotal)}</td><td></td></tr></tfoot>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Record Payment</DialogTitle><DialogDescription>Record a payment received against this project, optionally linked to an invoice.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Date</Label><Input data-testid="pay-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Amount (₹)</Label><Input data-testid="pay-amount" type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Mode</Label>
              <Select value={form.mode} onValueChange={(v) => set("mode", v)}><SelectTrigger data-testid="pay-mode"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.payment_modes || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Reference No.</Label><Input data-testid="pay-reference" value={form.reference} onChange={(e) => set("reference", e.target.value)} placeholder="UTR / cheque no." /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs font-semibold text-slate-600">Against Invoice (optional)</Label>
              <Select value={form.invoice_id || "none"} onValueChange={(v) => set("invoice_id", v === "none" ? "" : v)}><SelectTrigger data-testid="pay-invoice"><SelectValue placeholder="Not linked" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Not linked to an invoice</SelectItem>{invoices.map((i) => <SelectItem key={i.id} value={i.id}>{i.invoice_no} · {formatINRFull(i.total)}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs font-semibold text-slate-600">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="pay-submit" onClick={submit} disabled={saving} className="font-semibold">{saving ? "Saving…" : "Record Payment"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!del} onOpenChange={() => setDel(null)} title="Delete payment?" description="This removes the payment record." confirmText="Delete" onConfirm={doDelete} />
    </div>
  );
}

function SummaryCard({ label, value, color = "#102A43" }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-1 font-mono text-base font-bold" style={{ color }}>{value}</div>
    </div>
  );
}
